import { useState, useEffect } from "react";
import {
  Box,
  Autocomplete,
  CircularProgress,
  TextField as MuiTextField,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { useNavigate, useParams } from "react-router";
import { useFormik } from "formik";
import * as Yup from "yup";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";
import PageHeader from "../../shared/PageHeader/index.tsx";
import TextField from "../../shared/TextField/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import CreateCustomerDialog from "../customers/CreateCustomerDialog.tsx";
import "../../styles/scss/Form.scss";

export default function VehicleCreateEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);

  const [customerOptions, setCustomerOptions] = useState<any[]>([]);
  const [customerSearching, setCustomerSearching] = useState(false);
  const [customerInput, setCustomerInput] = useState("");
  const [selectedCustomerObj, setSelectedCustomerObj] = useState<any>(null);

  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [initialCustomersLoaded, setInitialCustomersLoaded] = useState(false);

  const formik = useFormik({
    initialValues: {
      regNo: "",
      customerId: null as number | null,
      vehicleDetails: "",
    },
    validationSchema: Yup.object({
      regNo: Yup.string().required("Registration number is required"),
      customerId: Yup.mixed().required("Customer is required"),
    }),
    onSubmit: async (values) => {
      try {
        let response;
        if (id) {
          response = await request.put("/api/updateVehicle/" + id, values);
        } else {
          response = await request.post("/api/createVehicle", values);
        }

        if (response?.data?.status) {
          toast.success("Vehicle saved successfully!");
          navigate("/admin/vehicles");
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            "An error occurred. Please try again.",
        );
      }
    },
  });

  useEffect(() => {
    if (id) {
      const fetchVehicle = async () => {
        setLoading(true);
        try {
          const response = await request.get(`/api/getVehicleById/${id}`);
          const vehicle = response.data.data;
          formik.setValues({
            regNo: vehicle.regNo || "",
            customerId: vehicle.customerId || null,
            vehicleDetails: vehicle.vehicleDetails || "",
          });
          if (vehicle.customer) {
            setSelectedCustomerObj(vehicle.customer);
            setCustomerOptions([vehicle.customer]);
            setCustomerInput(vehicle.customer.name || "");
          }
          setLoading(false);
        } catch (error) {
          console.log(error);
          setLoading(false);
        }
      };

      fetchVehicle();
    }
  }, [id]);

  useEffect(() => {
    const query = customerInput;
    if (!query || query.length < 2) {
      if (selectedCustomerObj) {
        setCustomerOptions([selectedCustomerObj]);
      } else if (!initialCustomersLoaded) {
        fetchInitialCustomers();
      } else {
        setCustomerOptions([]);
      }
      return;
    }
    const timer = setTimeout(async () => {
      setCustomerSearching(true);
      try {
        const res = await request.get("/api/searchCustomer", { phone: query });
        const fetched = res.data.data || [];
        if (
          selectedCustomerObj &&
          !fetched.some((c: any) => c.id === selectedCustomerObj.id)
        ) {
          fetched.unshift(selectedCustomerObj);
        }
        setCustomerOptions(fetched);
      } catch {
        setCustomerOptions(selectedCustomerObj ? [selectedCustomerObj] : []);
      } finally {
        setCustomerSearching(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [customerInput]);

  const fetchInitialCustomers = async () => {
    setCustomerSearching(true);
    try {
      const res = await request.get("/api/searchCustomer", { phone: "" });
      setCustomerOptions(res.data.data || []);
      setInitialCustomersLoaded(true);
    } catch {
      setCustomerOptions([]);
    } finally {
      setCustomerSearching(false);
    }
  };

  return (
    <PageWrapper loading={loading}>
      <Box className="form-container">
        <Box className="form-page">
          <Box className="form-header">
            <PageHeader
              title={id && id !== "new" ? "Edit Vehicle" : "Create New Vehicle"}
              secondaryButton={{
                label: "Cancel",
                onClick: () => navigate("/admin/vehicles"),
              }}
              primaryButton={{
                label: id && id !== "new" ? "Update Vehicle" : "Create Vehicle",
                icon: <span className="material-symbols-outlined">save</span>,
                onClick: () => formik.handleSubmit(),
              }}
            />
          </Box>

          <Box className="form-card">
            <Box className="form-card-body">
              <Box className="form-grid form-grid--2">
                <Box className="form-field">
                  <MuiTextField
                    name="regNo"
                    label="Registration No"
                    size="small"
                    fullWidth
                    value={formik.values.regNo}
                    onChange={(e) => {
                      const raw = e.target.value
                        .replace(/[^a-zA-Z0-9]/g, "")
                        .toUpperCase();
                      formik.setFieldValue("regNo", raw);
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.regNo && Boolean(formik.errors.regNo)}
                    helperText={
                      formik.touched.regNo && (formik.errors.regNo as string)
                    }
                    autoFocus
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        "&:hover fieldset": { borderColor: "#e53935" },
                        "&.Mui-focused fieldset": { borderColor: "#e53935" },
                      },
                      "& .MuiInputLabel-root.Mui-focused": { color: "#e53935" },
                      mt: 0,
                      mb: 0,
                    }}
                  />
                </Box>
                <Box className="form-field">
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={customerOptions}
                    loading={customerSearching}
                    inputValue={customerInput}
                    onFocus={() => {
                      if (!initialCustomersLoaded && !selectedCustomerObj) {
                        fetchInitialCustomers();
                      }
                    }}
                    filterOptions={(options, { inputValue }) => {
                      const filtered = options.filter(
                        (opt) =>
                          opt.name
                            ?.toLowerCase()
                            .includes(inputValue.toLowerCase()) ||
                          opt.phone?.includes(inputValue),
                      );
                      if (inputValue && filtered.length === 0) {
                        filtered.push({
                          id: "__create__",
                          name: inputValue,
                          phone: "",
                          _createNew: true,
                        });
                      }
                      return filtered;
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    getOptionLabel={(option: any) => {
                      if (option._createNew) {
                        return `Create customer "${option.name}"`;
                      }
                      return `${option.name}${option.phone ? ` — ${option.phone}` : ""}`;
                    }}
                    value={selectedCustomerObj}
                    onChange={(_e, value) => {
                      if (value?._createNew) {
                        setCustomerDialogOpen(true);
                      } else if (value) {
                        formik.setFieldValue("customerId", value.id);
                        setSelectedCustomerObj(value);
                        setCustomerInput(value.name || "");
                      } else {
                        formik.setFieldValue("customerId", null);
                        setSelectedCustomerObj(null);
                        setCustomerInput("");
                      }
                    }}
                    onInputChange={(_e, value) => {
                      setCustomerInput(value);
                      if (!value) {
                        formik.setFieldValue("customerId", null);
                      }
                    }}
                    renderInput={(params) => (
                      <MuiTextField
                        {...params}
                        label="Customer"
                        name="customerId"
                        error={
                          formik.touched.customerId &&
                          Boolean(formik.errors.customerId)
                        }
                        helperText={
                          formik.touched.customerId && formik.errors.customerId
                            ? String(formik.errors.customerId)
                            : ""
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {customerSearching && (
                                <CircularProgress size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option: any) => {
                      if (option._createNew) {
                        return (
                          <Box
                            component="li"
                            {...props}
                            sx={{
                              color: "primary.main",
                              fontWeight: 600,
                              gap: 1,
                              borderTop: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <PersonAddIcon fontSize="small" />
                            {`Create customer "${option.name}"`}
                          </Box>
                        );
                      }
                      return (
                        <Box
                          component="li"
                          {...props}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 1,
                          }}
                        >
                          <span>{option.name}</span>
                          <span
                            style={{ color: "#94a3b8", fontSize: "0.8rem" }}
                          >
                            {option.phone}
                          </span>
                        </Box>
                      );
                    }}
                  />
                </Box>
              </Box>
              <Box
                className="form-grid form-grid--2"
                style={{ marginTop: "16px" }}
              >
                <Box className="form-field">
                  <TextField
                    name="vehicleDetails"
                    label="Vehicle Details"
                    type="text"
                    multiline
                    rows={3}
                    formik={formik}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <CreateCustomerDialog
        open={customerDialogOpen}
        onClose={() => setCustomerDialogOpen(false)}
        onSuccess={(customer) => {
          if (customer) {
            setSelectedCustomerObj(customer);
            setCustomerOptions((prev) => {
              const updated = prev.filter((c) => c.id !== "__create__");
              if (!updated.some((c) => c.id === customer.id)) {
                updated.unshift(customer);
              }
              return updated;
            });
            formik.setFieldValue("customerId", customer.id);
            setCustomerInput(customer.name || "");
          }
        }}
      />
    </PageWrapper>
  );
}
